import {
  addDoc,
  arrayRemove,
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
  type DocumentData,
  type QueryConstraint,
  type Unsubscribe,
} from 'firebase/firestore';
import { db } from '../../../shared/storage/firebase';
import type { Squad } from '../../../types';
import {
  PARTNER_SCHEMA_VERSION,
  type Partner,
  type PartnerCalendarEntry,
  type PartnerContact,
  type PartnerLocation,
  type PartnerRequirement,
  type PartnerSeason,
  type OrganizationSeason,
  type PartnerSeasonFlow,
  type PartnerTeacherClearance,
  type ScopedDocument,
  type ScopedNote,
} from '../types';

export const PARTNER_COLLECTION = 'partners';
export const LOCATION_COLLECTION = 'locations';
export const PARTNER_SEASON_FLOW_COLLECTION = 'partnerSeasonFlows';
export const PUBLIC_LOCATION_COLLECTION = 'publicLocations';
export const ORGANIZATION_SEASON_COLLECTION = 'organizationSeasons';

type RecordWithId = { id: string };

function withoutUndefined(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(withoutUndefined);
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [key, child] of Object.entries(value as Record<string, unknown>)) {
      if (child !== undefined) out[key] = withoutUndefined(child);
    }
    return out;
  }
  return value;
}

function dataForWrite(value: Record<string, unknown>): DocumentData {
  return withoutUndefined(value) as DocumentData;
}

function fromSnapshot<T extends RecordWithId>(snapshot: { id: string; data(): DocumentData | undefined }): T {
  return { id: snapshot.id, ...(snapshot.data() || {}) } as T;
}

async function listSubcollection<T extends RecordWithId>(
  partnerId: string,
  subcollection: string,
  constraints: QueryConstraint[] = []
): Promise<T[]> {
  const ref = collection(db, PARTNER_COLLECTION, partnerId, subcollection);
  const snapshot = await getDocs(query(ref, ...constraints));
  return snapshot.docs.map((item) => fromSnapshot<T>(item));
}

async function createSubcollectionDocument<T extends RecordWithId>(
  partnerId: string,
  subcollection: string,
  value: Omit<T, 'id'>,
  actorId?: string | null
): Promise<T> {
  const nowFields = {
    createdAt: serverTimestamp(),
    createdBy: actorId || null,
    updatedAt: serverTimestamp(),
    updatedBy: actorId || null,
  };
  const ref = await addDoc(
    collection(db, PARTNER_COLLECTION, partnerId, subcollection),
    dataForWrite({ ...(value as Record<string, unknown>), ...nowFields })
  );
  const snapshot = await getDoc(ref);
  return fromSnapshot<T>(snapshot);
}

async function updateSubcollectionDocument<T extends RecordWithId>(
  partnerId: string,
  subcollection: string,
  documentId: string,
  patch: Partial<Omit<T, 'id'>>,
  actorId?: string | null
): Promise<void> {
  await updateDoc(
    doc(db, PARTNER_COLLECTION, partnerId, subcollection, documentId),
    dataForWrite({
      ...(patch as Record<string, unknown>),
      updatedAt: serverTimestamp(),
      updatedBy: actorId || null,
    })
  );
}

export async function listPartners(): Promise<Partner[]> {
  const snapshot = await getDocs(query(collection(db, PARTNER_COLLECTION), orderBy('name', 'asc')));
  return snapshot.docs.map((item) => fromSnapshot<Partner>(item));
}

export async function listOrganizationSeasons(): Promise<OrganizationSeason[]> {
  const snapshot = await getDocs(collection(db, ORGANIZATION_SEASON_COLLECTION));
  return snapshot.docs.map((item) => fromSnapshot<OrganizationSeason>(item));
}

export async function saveOrganizationSeason(
  termId: string,
  value: Omit<OrganizationSeason, 'id' | 'updatedAt' | 'updatedBy'>,
  actorId?: string | null
): Promise<void> {
  await setDoc(
    doc(db, ORGANIZATION_SEASON_COLLECTION, termId),
    dataForWrite({
      ...value,
      termId,
      updatedAt: serverTimestamp(),
      updatedBy: actorId || null,
    }),
    { merge: true }
  );
}

export function subscribeToPartners(
  onValue: (partners: Partner[]) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  return onSnapshot(
    query(collection(db, PARTNER_COLLECTION), orderBy('name', 'asc')),
    (snapshot) => onValue(snapshot.docs.map((item) => fromSnapshot<Partner>(item))),
    (error) => onError?.(error)
  );
}

export function subscribeToPartnerLocations(
  onValue: (locations: PartnerLocation[]) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  return onSnapshot(
    query(collection(db, LOCATION_COLLECTION), orderBy('name', 'asc')),
    (snapshot) => onValue(snapshot.docs.map((item) => fromSnapshot<PartnerLocation>(item))),
    (error) => onError?.(error)
  );
}

export async function getPartner(partnerId: string): Promise<Partner | null> {
  const snapshot = await getDoc(doc(db, PARTNER_COLLECTION, partnerId));
  return snapshot.exists() ? fromSnapshot<Partner>(snapshot) : null;
}

export async function createPartner(
  value: Omit<Partner, 'id' | 'schemaVersion' | 'createdAt' | 'updatedAt'>,
  actorId?: string | null
): Promise<Partner> {
  const ref = await addDoc(
    collection(db, PARTNER_COLLECTION),
    dataForWrite({
      ...value,
      schemaVersion: PARTNER_SCHEMA_VERSION,
      createdAt: serverTimestamp(),
      createdBy: actorId || null,
      updatedAt: serverTimestamp(),
      updatedBy: actorId || null,
    })
  );
  const snapshot = await getDoc(ref);
  return fromSnapshot<Partner>(snapshot);
}

export async function updatePartner(
  partnerId: string,
  patch: Partial<Omit<Partner, 'id' | 'schemaVersion' | 'createdAt'>>,
  actorId?: string | null
): Promise<void> {
  await setDoc(
    doc(db, PARTNER_COLLECTION, partnerId),
    dataForWrite({
      ...patch,
      schemaVersion: PARTNER_SCHEMA_VERSION,
      updatedAt: serverTimestamp(),
      updatedBy: actorId || null,
    }),
    { merge: true }
  );
}

const PARTNER_SUBCOLLECTIONS = [
  'contacts',
  'requirements',
  'seasons',
  'notes',
  'documents',
  'teacherClearances',
  'calendarEntries',
] as const;

async function commitDeletes(refs: Array<ReturnType<typeof doc>>): Promise<void> {
  let batch = writeBatch(db);
  let pending = 0;
  for (const ref of refs) {
    batch.delete(ref);
    pending += 1;
    if (pending >= 450) {
      await batch.commit();
      batch = writeBatch(db);
      pending = 0;
    }
  }
  if (pending > 0) await batch.commit();
}

export async function deletePartner(partnerId: string): Promise<void> {
  const [locations, flowSnapshot] = await Promise.all([
    listPartnerLocations(partnerId),
    getDocs(query(collection(db, PARTNER_SEASON_FLOW_COLLECTION), where('partnerId', '==', partnerId))),
  ]);
  const subcollectionSnapshots = await Promise.all(
    PARTNER_SUBCOLLECTIONS.map((name) => getDocs(collection(db, PARTNER_COLLECTION, partnerId, name)))
  );
  await commitDeletes([
    ...subcollectionSnapshots.flatMap((snapshot) => snapshot.docs.map((item) => item.ref)),
    ...flowSnapshot.docs.map((item) => item.ref),
    ...locations.map((location) => doc(db, LOCATION_COLLECTION, location.id)),
    doc(db, PARTNER_COLLECTION, partnerId),
  ]);
  await Promise.allSettled(
    locations.map((location) => deleteDoc(doc(db, PUBLIC_LOCATION_COLLECTION, location.id)))
  );
}

export async function listPartnerLocations(partnerId: string): Promise<PartnerLocation[]> {
  const snapshot = await getDocs(
    query(collection(db, LOCATION_COLLECTION), where('partnerId', '==', partnerId))
  );
  return snapshot.docs
    .map((item) => fromSnapshot<PartnerLocation>(item))
    .sort((left, right) => left.name.localeCompare(right.name));
}

export async function getPartnerLocation(locationId: string): Promise<PartnerLocation | null> {
  const snapshot = await getDoc(doc(db, LOCATION_COLLECTION, locationId));
  return snapshot.exists() ? fromSnapshot<PartnerLocation>(snapshot) : null;
}

export async function createPartnerLocation(
  value: Omit<PartnerLocation, 'id' | 'schemaVersion' | 'createdAt' | 'updatedAt'>,
  actorId?: string | null
): Promise<PartnerLocation> {
  const ref = await addDoc(
    collection(db, LOCATION_COLLECTION),
    dataForWrite({
      ...value,
      isPublic: value.isPublic !== false,
      schemaVersion: PARTNER_SCHEMA_VERSION,
      createdAt: serverTimestamp(),
      createdBy: actorId || null,
      updatedAt: serverTimestamp(),
      updatedBy: actorId || null,
    })
  );
  const snapshot = await getDoc(ref);
  return fromSnapshot<PartnerLocation>(snapshot);
}

export async function updatePartnerLocation(
  locationId: string,
  patch: Partial<Omit<PartnerLocation, 'id' | 'partnerId' | 'schemaVersion' | 'createdAt'>>,
  actorId?: string | null
): Promise<void> {
  await setDoc(
    doc(db, LOCATION_COLLECTION, locationId),
    dataForWrite({
      ...patch,
      schemaVersion: PARTNER_SCHEMA_VERSION,
      updatedAt: serverTimestamp(),
      updatedBy: actorId || null,
    }),
    { merge: true }
  );
}

export const listPartnerContacts = (partnerId: string) =>
  listSubcollection<PartnerContact>(partnerId, 'contacts', [orderBy('displayName', 'asc')]);

export async function listPartnerContactCounts(partnerIds: string[]): Promise<Record<string, number>> {
  const entries = await Promise.all(
    partnerIds.map(async (partnerId) => {
      const snapshot = await getDocs(collection(db, PARTNER_COLLECTION, partnerId, 'contacts'));
      return [partnerId, snapshot.size] as const;
    })
  );
  return Object.fromEntries(entries);
}

export const createPartnerContact = (
  partnerId: string,
  value: Omit<PartnerContact, 'id'>,
  actorId?: string | null
) => createSubcollectionDocument<PartnerContact>(partnerId, 'contacts', value, actorId);

export const updatePartnerContact = (
  partnerId: string,
  contactId: string,
  patch: Partial<Omit<PartnerContact, 'id'>>,
  actorId?: string | null
) => updateSubcollectionDocument<PartnerContact>(partnerId, 'contacts', contactId, patch, actorId);

export const deletePartnerContact = (partnerId: string, contactId: string) =>
  deleteDoc(doc(db, PARTNER_COLLECTION, partnerId, 'contacts', contactId));

export const listPartnerRequirements = (partnerId: string) =>
  listSubcollection<PartnerRequirement>(partnerId, 'requirements', [orderBy('label', 'asc')]);

export const createPartnerRequirement = (
  partnerId: string,
  value: Omit<PartnerRequirement, 'id'>,
  actorId?: string | null
) => createSubcollectionDocument<PartnerRequirement>(partnerId, 'requirements', value, actorId);

export const updatePartnerRequirement = (
  partnerId: string,
  requirementId: string,
  patch: Partial<Omit<PartnerRequirement, 'id'>>,
  actorId?: string | null
) => updateSubcollectionDocument<PartnerRequirement>(partnerId, 'requirements', requirementId, patch, actorId);

export const deletePartnerRequirement = (partnerId: string, requirementId: string) =>
  deleteDoc(doc(db, PARTNER_COLLECTION, partnerId, 'requirements', requirementId));

export const listPartnerSeasons = (partnerId: string) =>
  listSubcollection<PartnerSeason>(partnerId, 'seasons', [orderBy('termId', 'desc')]);

export async function savePartnerSeason(
  partnerId: string,
  seasonId: string,
  value: Omit<PartnerSeason, 'id' | 'partnerId' | 'createdAt' | 'updatedAt'>,
  actorId?: string | null
): Promise<void> {
  await setDoc(
    doc(db, PARTNER_COLLECTION, partnerId, 'seasons', seasonId),
    dataForWrite({
      ...value,
      partnerId,
      createdAt: serverTimestamp(),
      createdBy: actorId || null,
      updatedAt: serverTimestamp(),
      updatedBy: actorId || null,
    }),
    { merge: true }
  );
}

export const PARTNER_CALENDAR_ENTRY_SUBCOLLECTION = 'calendarEntries';

export const listPartnerCalendarEntries = (partnerId: string) =>
  listSubcollection<PartnerCalendarEntry>(partnerId, PARTNER_CALENDAR_ENTRY_SUBCOLLECTION, [orderBy('startDate', 'asc')]);

export const createPartnerCalendarEntry = (
  partnerId: string,
  value: Omit<PartnerCalendarEntry, 'id'>,
  actorId?: string | null
) => createSubcollectionDocument<PartnerCalendarEntry>(partnerId, PARTNER_CALENDAR_ENTRY_SUBCOLLECTION, value, actorId);

export const updatePartnerCalendarEntry = (
  partnerId: string,
  entryId: string,
  patch: Partial<Omit<PartnerCalendarEntry, 'id'>>,
  actorId?: string | null
) => updateSubcollectionDocument<PartnerCalendarEntry>(partnerId, PARTNER_CALENDAR_ENTRY_SUBCOLLECTION, entryId, patch, actorId);

export const deletePartnerCalendarEntry = (partnerId: string, entryId: string) =>
  deleteDoc(doc(db, PARTNER_COLLECTION, partnerId, PARTNER_CALENDAR_ENTRY_SUBCOLLECTION, entryId));

export const listPartnerNotes = (partnerId: string) =>
  listSubcollection<ScopedNote>(partnerId, 'notes', [orderBy('createdAt', 'desc')]);

export const createPartnerNote = (
  partnerId: string,
  value: Omit<ScopedNote, 'id'>,
  actorId?: string | null
) => createSubcollectionDocument<ScopedNote>(partnerId, 'notes', value, actorId);

export const listPartnerDocuments = (partnerId: string) =>
  listSubcollection<ScopedDocument>(partnerId, 'documents', [orderBy('createdAt', 'desc')]);

export const listPartnerTeacherClearances = (partnerId: string, maxResults = 250) =>
  listSubcollection<PartnerTeacherClearance>(partnerId, 'teacherClearances', [
    orderBy('cleared', 'desc'),
    limit(maxResults),
  ]);

export async function savePartnerTeacherClearance(
  partnerId: string,
  teacherId: string,
  value: Omit<PartnerTeacherClearance, 'id' | 'partnerId' | 'teacherId' | 'updatedAt' | 'updatedBy'>,
  actorId?: string | null
): Promise<void> {
  await setDoc(
    doc(db, PARTNER_COLLECTION, partnerId, 'teacherClearances', teacherId),
    dataForWrite({
      ...value,
      partnerId,
      teacherId,
      updatedAt: serverTimestamp(),
      updatedBy: actorId || null,
    }),
    { merge: true }
  );
}

export async function listPartnerSquads(partnerId: string): Promise<Squad[]> {
  const snapshot = await getDocs(
    query(collection(db, 'squads'), where('partnerId', '==', partnerId))
  );
  return snapshot.docs.map((item) => fromSnapshot<Squad>(item));
}

export async function updatePartnerSquad(
  squadId: string,
  patch: Partial<Pick<Squad, 'title' | 'termId' | 'term' | 'seasonId' | 'locationId' | 'locationName' | 'status' | 'visibility' | 'capacity' | 'scheduleStatus' | 'scheduleConfirmedAt' | 'scheduleConfirmedBy'>>,
  actorId?: string | null
): Promise<void> {
  await updateDoc(
    doc(db, 'squads', squadId),
    dataForWrite({
      ...patch,
      updatedAt: serverTimestamp(),
      updatedBy: actorId || null,
    })
  );
}

export async function updatePartnerSquadSeason(
  partnerId: string,
  squadId: string,
  previousTermId: string,
  nextTermId: string,
  nextTermLabel: string,
  locationId: string,
  actorId?: string | null
): Promise<void> {
  const previousSeasonRef = previousTermId ? doc(db, 'partners', partnerId, 'seasons', previousTermId) : null;
  const previousFlowRef = previousTermId ? doc(db, PARTNER_SEASON_FLOW_COLLECTION, `${partnerId}__${previousTermId}`) : null;
  const nextSeasonRef = nextTermId ? doc(db, 'partners', partnerId, 'seasons', nextTermId) : null;
  const nextFlowRef = nextTermId ? doc(db, PARTNER_SEASON_FLOW_COLLECTION, `${partnerId}__${nextTermId}`) : null;
  const [previousSeason, previousFlow, nextSeason, nextFlow] = await Promise.all([
    previousSeasonRef ? getDoc(previousSeasonRef) : null,
    previousFlowRef ? getDoc(previousFlowRef) : null,
    nextSeasonRef ? getDoc(nextSeasonRef) : null,
    nextFlowRef ? getDoc(nextFlowRef) : null,
  ]);
  const batch = writeBatch(db);
  batch.update(doc(db, 'squads', squadId), dataForWrite({
    termId: nextTermId,
    term: nextTermLabel,
    seasonId: nextTermId,
    updatedAt: serverTimestamp(),
    updatedBy: actorId || null,
  }));
  if (previousTermId && previousTermId !== nextTermId) {
    if (previousSeasonRef && previousSeason?.exists()) batch.set(previousSeasonRef, {
      linkedSquadIds: arrayRemove(squadId),
      updatedAt: serverTimestamp(),
      updatedBy: actorId || null,
    }, { merge: true });
    if (previousFlowRef && previousFlow?.exists()) batch.set(previousFlowRef, {
      squadIds: arrayRemove(squadId),
      updatedAt: serverTimestamp(),
      updatedBy: actorId || null,
    }, { merge: true });
  }
  if (nextSeasonRef) {
    batch.set(nextSeasonRef, {
      partnerId,
      termId: nextTermId,
      label: nextTermLabel,
      ...(locationId
        ? { linkedLocationIds: arrayUnion(locationId) }
        : (!nextSeason?.exists() ? { linkedLocationIds: [] } : {})),
      linkedSquadIds: arrayUnion(squadId),
      ...(!nextSeason?.exists() ? { createdAt: serverTimestamp(), createdBy: actorId || null } : {}),
      updatedAt: serverTimestamp(),
      updatedBy: actorId || null,
    }, { merge: true });
  }
  if (nextFlowRef) {
    batch.set(nextFlowRef, {
      partnerId,
      seasonId: nextTermId,
      ...(locationId
        ? { locationIds: arrayUnion(locationId) }
        : (!nextFlow?.exists() ? { locationIds: [] } : {})),
      squadIds: arrayUnion(squadId),
      ...(!nextFlow?.exists() ? { createdAt: serverTimestamp(), createdBy: actorId || null } : {}),
      updatedAt: serverTimestamp(),
      updatedBy: actorId || null,
    }, { merge: true });
  }
  await batch.commit();
}

export async function getPartnerSeasonFlow(partnerId: string, seasonId: string): Promise<PartnerSeasonFlow | null> {
  const flowId = `${partnerId}__${seasonId}`;
  const snapshot = await getDoc(doc(db, PARTNER_SEASON_FLOW_COLLECTION, flowId));
  return snapshot.exists() ? fromSnapshot<PartnerSeasonFlow>(snapshot) : null;
}

export async function savePartnerSeasonFlow(
  partnerId: string,
  seasonId: string,
  patch: Partial<Omit<PartnerSeasonFlow, 'id' | 'partnerId' | 'seasonId' | 'createdAt'>>,
  actorId?: string | null
): Promise<string> {
  const flowId = `${partnerId}__${seasonId}`;
  await setDoc(
    doc(db, PARTNER_SEASON_FLOW_COLLECTION, flowId),
    dataForWrite({
      ...patch,
      partnerId,
      seasonId,
      createdAt: serverTimestamp(),
      createdBy: actorId || null,
      updatedAt: serverTimestamp(),
      updatedBy: actorId || null,
    }),
    { merge: true }
  );
  return flowId;
}
